import { useEffect, useRef, useState } from "react";
import './IAChat.scss'
import { useTranslation } from "react-i18next";

export type IAChatProps = {
    machineId: number;
    predefinedQuestions: PredefinedQuestions[];
};

export type PredefinedQuestions = {
    question: string;
    response: string;
};

type Msg = { role: "human" | "AI"; text: string; ts: number };

export default function IAChat({
    machineId,
    predefinedQuestions
}: IAChatProps) {
    const [open, setOpen] = useState(false);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [messages, setMessages] = useState<Msg[]>([]);
    const listRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const { t } = useTranslation();

    const sendMessage = (q: PredefinedQuestions) => {
        if (!q.question.trim()) return;

        setLoading(true);

        setMessages((prev) => [
            ...prev,
            { role: "human", text: t(q.question), ts: Date.now() }
        ]);

        setTimeout(() => {
            setMessages((prev) => [
                ...prev,
                { role: "AI", text: t(q.response), ts: Date.now() }
            ]);
            setLoading(false);
        }, 1000);
    };

    useEffect(() => {
        setMessages([])
    }, [machineId])

    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
    }, [messages, loading]);

    useEffect(() => {
        if (open) {
            const id = setTimeout(() => inputRef.current?.focus(), 50);
            return () => clearTimeout(id);
        }
    }, [open, loading]);


    const onSubmit = (e: any) => {
        e.preventDefault();
        setLoading(true);
        setInput('')
        setMessages((prev) => [...prev, {
            role:
                "human", text: input, ts: Date.now()
        }]);
        const genericReply = getGenericResponse(input);

        setTimeout(() => {
            setMessages((prev) => [
                ...prev,
                {
                    role: "AI",
                    text: genericReply ?? t('ThisIsADemoChatBot'),
                    ts: Date.now()
                }
            ]);
            setLoading(false);
        }, 1000);
    }

    const normalize = (text: string) =>
        text
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "") // treu accents
            .trim();

    const getGenericResponse = (input: string): string | null => {
        const text = normalize(input);

        const patterns = [
            // Castellano
            {
                lang: "es",
                match: /^(hola|buenos dias|buenas tardes|buenas noches|que tal|como estas|adios|gracias)/,
                responses: {
                    hola: "¡Hola! 😊 ¿En qué puedo ayudarte?",
                    adios: "¡Adiós! 👋 Que tengas un buen día.",
                    gracias: "¡De nada! 😊",
                    default: "Encantado de ayudarte 😊"
                }
            },
            // Català
            {
                lang: "ca",
                match: /^(hola|bon dia|bona tarda|bona nit|que tal|com estas|adeu|gracies)/,
                responses: {
                    hola: "Hola! 😊 En què et puc ajudar?",
                    adeu: "Adeu! 👋 Que vagi molt bé!",
                    gracies: "De res! 😊",
                    default: "Encantat d'ajudar-te! 😊"
                }
            },
            // English
            {
                lang: "en",
                match: /^(hello|hi|good morning|good afternoon|good evening|how are you|bye|thanks|thank you)/,
                responses: {
                    hello: "Hello! 😊 How can I help you?",
                    bye: "Goodbye! 👋 Have a great day!",
                    thanks: "You're welcome! 😊",
                    default: "Happy to help! 😊"
                }
            },
            // Français
            {
                lang: "fr",
                match: /^(bonjour|bonsoir|salut|ca va|au revoir|merci)/,
                responses: {
                    bonjour: "Bonjour ! 😊 Comment puis-je vous aider ?",
                    "au revoir": "Au revoir ! 👋 Bonne journée !",
                    merci: "Avec plaisir ! 😊",
                    default: "Ravi de vous aider 😊"
                }
            }
        ];

        for (const p of patterns) {
            if (p.match.test(text)) {
                for (const key of Object.keys(p.responses)) {
                    if (text.startsWith(key)) {
                        return (p.responses as any)[key];
                    }
                }
                return p.responses.default;
            }
        }

        return null;
    };



    return (
        <>
            {!open && (
                <div className='iaChatButtonContainer'>
                    <button
                        onClick={() => setOpen(true)}
                        aria-controls="example-collapse-text"
                        className="btn-Custom btn-IDGreen iaChatButton"
                    >
                        <i className="bi bi-stars"></i>
                    </button>
                </div>
            )}

            {open && (
                <div className="iaChatModal chat-widget slide-in">
                    <div className="chat-header">
                        <div className="d-flex align-items-center gap-2">
                            <div className="avatar">🤖</div>
                            <div className="lh-1">
                                <strong>{t('IAAssistant')}</strong>
                                <div className="status"><span className="dot" /> {t('Online')}</div>
                            </div>
                        </div>

                        <button onClick={() => setOpen(false)} className="btn-icon" aria-label="Cerrar">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                            </svg>
                        </button>
                    </div>


                    <div className="predefined-questions">
                        {predefinedQuestions.map((q, i) => (
                            <button
                                key={i}
                                className="predefined-btn"
                                onClick={() => sendMessage(q)}
                            >
                                {t(q.question)}
                            </button>
                        ))}
                    </div>


                    <div ref={listRef} className="chat-body" id="chatMessages">
                        {
                            messages.map((m) => (<div className={`message ${m.role}`}>{m.text}</div>))
                        }

                    </div>

                    <form className="chat-input" onSubmit={onSubmit}>
                        {!loading ? <><input
                            type="text"
                            value={input}
                            ref={inputRef}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder={t('Typeyourmessage...')}
                        />
                            <button type="submit" disabled={!input.trim()}>
                                {t('Send')}
                            </button></> :
                            <div className="loading-wrap d-flex justify-content-center" aria-live="polite" aria-busy="true">
                                <div className="loading-dots">
                                    <span className="dot" />
                                    <span className="dot" />
                                    <span className="dot" />
                                </div>
                            </div>}
                    </form>

                </div>

            )}
        </>
    );
}